import { supabase } from "../supabase";

// Short-lived cache, keyed by the calling user — every conditionally-mounted
// component that needs this list (Comments.jsx opens/closes fresh per post,
// FollowListModal, Search, several feed pages, ...) used to re-run 2 queries
// on every single mount, even though the underlying blocks table barely
// changes within one browsing session. A 15s TTL absorbs that repeated-mount
// pattern (e.g. opening comments on several posts while scrolling) while
// still staying fresh enough for callers (NotificationBell's openPanel, most
// notably) that explicitly want a just-made block reflected quickly.
const CACHE_TTL_MS = 15000;
const blockedIdsCache = new Map(); // userId -> { ids, expiresAt }

function invalidateBlockedIdsCache(userId) {
  blockedIdsCache.delete(userId);
}

// Combined list of "people I blocked" + "people who blocked me" — used to
// filter both directions out of feeds, since a block should hide content
// from each other regardless of who blocked whom.
export async function getBlockedUserIds(userId) {
  if (!userId) return [];

  const cached = blockedIdsCache.get(userId);
  if (cached && cached.expiresAt > Date.now()) return cached.ids;

  const [
    { data: iBlocked, error: iBlockedError },
    { data: blockedMe, error: blockedMeError }
  ] = await Promise.all([
    supabase.from("blocks").select("blocked_id").eq("blocker_id", userId),
    supabase.from("blocks").select("blocker_id").eq("blocked_id", userId)
  ]);

  if (iBlockedError) console.log("GET BLOCKED IDS (i blocked) ERROR:", iBlockedError);
  if (blockedMeError) console.log("GET BLOCKED IDS (blocked me) ERROR:", blockedMeError);

  const ids = new Set();
  (iBlocked || []).forEach(r => ids.add(r.blocked_id));
  (blockedMe || []).forEach(r => ids.add(r.blocker_id));
  const idsArray = [...ids];
  blockedIdsCache.set(userId, { ids: idsArray, expiresAt: Date.now() + CACHE_TTL_MS });
  return idsArray;
}

export async function blockUser(blockerId, blockedId) {
  // Goes through a SECURITY DEFINER RPC rather than a direct insert+delete —
  // "follows" RLS only lets a row's own follower delete it, so a plain
  // client call could remove the blocker's own follow but never the reverse
  // direction (the blocked person still following the blocker). That left
  // them still counted in the blocker's public follower count even though
  // FollowListModal already filtered them out of the visible list, so the
  // number and the list disagreed. The RPC runs as its owner, so it can
  // delete both directions in one step.
  const { error } = await supabase.rpc("block_user_and_unfollow", {
    p_blocked_id: blockedId
  });

  if (!error) invalidateBlockedIdsCache(blockerId);
  return { error };
}

export async function unblockUser(blockerId, blockedId) {
  const { error } = await supabase
    .from("blocks")
    .delete()
    .eq("blocker_id", blockerId)
    .eq("blocked_id", blockedId);

  if (!error) invalidateBlockedIdsCache(blockerId);
  return { error };
}

export async function checkBlockStatus(currentUserId, otherUserId) {
  if (!currentUserId || !otherUserId) {
    return { blockedByMe: false, blockedMe: false };
  }

  const [
    { data: mine, error: mineError },
    { data: theirs, error: theirsError }
  ] = await Promise.all([
    supabase
      .from("blocks")
      .select("id")
      .eq("blocker_id", currentUserId)
      .eq("blocked_id", otherUserId)
      .maybeSingle(),
    supabase
      .from("blocks")
      .select("id")
      .eq("blocker_id", otherUserId)
      .eq("blocked_id", currentUserId)
      .maybeSingle()
  ]);

  if (mineError) console.log("CHECK BLOCK STATUS (mine) ERROR:", mineError);
  if (theirsError) console.log("CHECK BLOCK STATUS (theirs) ERROR:", theirsError);

  return { blockedByMe: !!mine, blockedMe: !!theirs };
}

export async function submitReport({ reporterId, targetType, targetId, reason }) {
  const { error } = await supabase.from("reports").insert({
    reporter_id: reporterId,
    target_type: targetType,
    target_id: targetId,
    reason: reason || null
  });

  return { error };
}
